import { ServicesTemplateCreateOrUpdateCommand } from '@dreryk/egabinet-private-medical-service-frontend-api';
import { VisitTemplatePrescriptionOnlyCreateOrUpdateCommand } from '@dreryk/egabinet-visit-frontend-api';
import { Icd10Code, Icd9Code } from '@dreryk/egabinet-icd-dictionary-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import {
   ActionEventInterceptor,
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
   dispatchSnackbarWarningEvent,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';

import { prescriptionVisitTemplatesTranslationKeys } from '../translations';

import { PRESCRIPTION_ONLY_TEMPLATE_TAG } from './PrescriptionVisitTemplates.constants';
import {
   PrescriptionVisitTemplatesFormBean,
   PrescriptionVisitTemplatesFormController,
} from './PrescriptionVisitTemplatesForm.types';

type PrescriptionVisitTemplatesSaveActionInterceptorProps = Pick<
   PrescriptionVisitTemplatesFormController,
   'handleSubmit'
> & {
   readonly children: JSX.Element;
};

export const PrescriptionVisitTemplatesSaveActionInterceptor = (
   props: PrescriptionVisitTemplatesSaveActionInterceptorProps
): JSX.Element => {
   const performingPrivateMedicalServicesAvailable = protectFeature('performingPrivateMedicalServices');

   const { executeAsync: saveVisitTemplate } = useCqrsCommand<void, PrescriptionVisitTemplatesFormBean>(
      (parameters) => {
         const command = new VisitTemplatePrescriptionOnlyCreateOrUpdateCommand();

         command.interview = Choice.withUseDefaultIfNull(parameters.interview?.getFormattedValue() ?? null);
         command.physicalExamination = Choice.withUseDefaultIfNull(
            parameters.physicalExamination?.getFormattedValue() ?? null
         );
         command.recommendations = Choice.withUseDefaultIfNull(parameters.recommendations?.getFormattedValue() ?? null);
         command.diagnosis = Choice.withUseDefaultIfNull(
            parameters.diagnosis ? Icd10Code.of(parameters.diagnosis.code) : null
         );
         command.procedure = Choice.withUseDefaultIfNull(
            parameters.procedure ? Icd9Code.of(parameters.procedure.code) : null
         );

         return command;
      }
   );

   const { executeAsync: saveServicesTemplate } = useCqrsCommand<void, PrescriptionVisitTemplatesFormBean>(
      (parameters) => {
         const command = new ServicesTemplateCreateOrUpdateCommand();
         command.templateTag = PRESCRIPTION_ONLY_TEMPLATE_TAG;
         command.medicalServiceId = Choice.withUseDefaultIfNull(parameters.medicalService?.medicalServiceId ?? null);
         command.receptionServiceId = Choice.withUseDefaultIfNull(
            parameters.receptionService?.receptionServiceId ?? null
         );
         return command;
      }
   );

   const handleSave = props.handleSubmit(async (parameters) => {
      const results = await Promise.allSettled([
         saveVisitTemplate(parameters),
         ...(performingPrivateMedicalServicesAvailable ? [saveServicesTemplate(parameters)] : []),
      ]);
      const failure = results.find((result) => result.status === 'rejected');

      if (!failure) {
         dispatchSnackbarPositiveEvent(prescriptionVisitTemplatesTranslationKeys.saveSuccess);
         return;
      }

      if (results.some((result) => result.status === 'fulfilled')) {
         dispatchSnackbarWarningEvent(prescriptionVisitTemplatesTranslationKeys.savePartialSuccess);
      } else {
         dispatchSnackbarNegativeEvent(prescriptionVisitTemplatesTranslationKeys.saveFailure);
      }

      throw failure.reason;
   });

   return (
      <ActionEventInterceptor action={handleSave} blockingOperation>
         {props.children}
      </ActionEventInterceptor>
   );
};
